import { Cell, Pie, PieChart } from "recharts";
import { HAZARD_LABELS, hazardKeyOf, hazardKeysFor, type AssetHazardResult } from "@/lib/results";
import { LEVEL_LABELS } from "@/lib/level-badge";
import { RISK_MARKER_COLORS } from "@/lib/leaflet-setup";
import { PendingScore } from "./PendingScore";
import { NotApplicableScore } from "./NotApplicableScore";
import { SeasonTag } from "./StatusCell";
import type { HazardType, Score } from "@shared/scoring/types";

const GAUGE_SIZE = 120;
/** This app's `--muted` token (client/src/index.css, light mode — the app is
 * light-mode-only, see docs/design-guidelines.md), as a literal color for
 * recharts' `fill` (which doesn't resolve CSS custom properties/Tailwind
 * classes). Used as the gauge's empty "track," never as a data color. */
const TRACK_COLOR = "hsl(186 15% 92%)";

interface HazardGaugeRowProps {
  results: AssetHazardResult[];
  /** The hazards to render, already narrowed by the Hazard filter (see
   * orderedHazards in results-filters.ts). Gauges are enumerated from this
   * list rather than from `results` so an unscored hazard still shows a
   * "Pending" gauge instead of silently disappearing. */
  hazards: HazardType[];
}

interface GaugeStat {
  avg: number;
  band: Score;
  count: number;
}

/** Average risk (not %-at-high-risk — that's HazardRiskBarChart's metric;
 * this is a distinct, complementary number, per functional spec's "semicircle
 * gauge per hazard" visual requirement). Rounds to the nearest whole Score
 * for color-banding, matching this app's Low/Medium/High/Very High scale. */
function averageRisk(rows: AssetHazardResult[]): GaugeStat | null {
  const computed = rows.filter((r) => r.status === "computed" && r.risk !== undefined);
  if (computed.length === 0) return null;

  const avg = computed.reduce((sum, r) => sum + (r.risk ?? 0), 0) / computed.length;
  const band = Math.min(4, Math.max(1, Math.round(avg))) as Score;
  return { avg, band, count: computed.length };
}

/** A semicircle "progress" gauge built from a 2-slice recharts Pie (value +
 * an invisible remainder up to the 1-4 scale's max of 4). Colored by the
 * Level-color scale's solid-fill hex values (same ones the map uses) rather
 * than a plain fixed color, since the arc IS the risk-level indicator here.
 * A native `title` tooltip is used instead of recharts' <Tooltip> — the
 * remainder slice isn't a real data category, and recharts' default tooltip
 * would show it as if it were one. */
function SemicircleGauge({ avg, band, count }: GaugeStat) {
  const data = [
    { name: "value", value: avg },
    { name: "remainder", value: 4 - avg },
  ];
  const assetWord = count === 1 ? "asset" : "assets";

  return (
    <div
      className="relative"
      style={{ width: GAUGE_SIZE, height: GAUGE_SIZE / 2 + 8 }}
      title={`Average risk ${avg.toFixed(1)}/4 (${LEVEL_LABELS[band]}) across ${count} ${assetWord}`}
    >
      <PieChart width={GAUGE_SIZE} height={GAUGE_SIZE / 2 + 8}>
        <Pie
          data={data}
          dataKey="value"
          startAngle={180}
          endAngle={0}
          cx="50%"
          cy={GAUGE_SIZE / 2}
          innerRadius={GAUGE_SIZE * 0.32}
          outerRadius={GAUGE_SIZE * 0.48}
          cornerRadius={4}
          stroke="none"
          isAnimationActive={false}
        >
          <Cell fill={RISK_MARKER_COLORS[band]} />
          <Cell fill={TRACK_COLOR} />
        </Pie>
      </PieChart>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 text-center">
        <p className="text-base font-semibold leading-none">{avg.toFixed(1)}</p>
        <p className="text-[10px] text-muted-foreground">{LEVEL_LABELS[band]}</p>
      </div>
    </div>
  );
}

/** One semicircle gauge per hazard (functional spec Tab 2 visuals),
 * showing average risk across that hazard's `"computed"` rows. Changing
 * Precipitation's tile holds one gauge per season line (MAM, OND, and a
 * "main season" gauge when Malawi assets are present), never one combined
 * average. Hazards with no computed rows yet (when this workspace's assets
 * don't have the needed data) fall back to the shared PendingScore pill
 * instead of a gauge with nothing to show. */
/**
 * Flex-wrap, not a fixed-column grid — same reasoning as TakeawayCards.tsx:
 * `lg:grid-cols-6` responds to viewport width, not this container's actual
 * width, and cramming all 6 gauges into a HorizonComparisonGrid column that
 * might only be a third of the viewport wide. `min-w` lets gauges wrap onto
 * a new line once the real available width runs out.
 */
export function HazardGaugeRow({ results, hazards }: HazardGaugeRowProps) {
  return (
    <div className="flex flex-wrap gap-3">
      {hazards.map((hazardType) => {
        const keys = hazardKeysFor([hazardType], results);
        return (
          <div
            key={hazardType}
            className="flex min-w-[130px] flex-1 flex-col items-center gap-2 rounded-xl border border-border bg-card p-3 text-center"
          >
            <p className="text-xs font-medium text-muted-foreground">{HAZARD_LABELS[hazardType]}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {keys.map((key) => {
                const hazardRows = results.filter((r) => hazardKeyOf(r) === key);
                const stat = averageRisk(hazardRows);
                // Every row "not applicable" (e.g. all assets on Landslide LHASA class
                // 0-2) is a definitive answer, not a gap - say so instead of "Pending".
                const allNotApplicable = hazardRows.length > 0 && hazardRows.every((r) => r.status === "not_applicable");
                return (
                  <div key={key} className="flex flex-col items-center gap-1">
                    {(keys.length > 1 || key.endsWith(":main")) && <SeasonTag season={key.split(":")[1]} />}
                    {stat ? (
                      <SemicircleGauge {...stat} />
                    ) : allNotApplicable ? (
                      <NotApplicableScore />
                    ) : (
                      <PendingScore label="Pending" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
